/**
 * Space Actions - Canonical Mutation API for Windows
 * 
 * All window operations flow through these actions, which then use
 * the internal _applyLocalWindow* helpers.
 * 
 * All actions handle:
 * - CRDT synchronization
 * - Backend persistence
 * - State invariants (activeWindowId, z-index, etc.)
 */

import { NormalizedWindowEntity } from './spaceStore';
import { createFileLogger } from '../utils/logger';
import {
  generateWindowId,
  resolveDefaultWindowSize,
  computeSmartWindowPosition,
  ENTITY_TYPE_WINDOW_SIZES,
} from '../components/Space/utils/windowUtils';
import { entityActions } from '../stores/entityActions';
import { entityRegistry } from '../stores/entityRegistry';
import { upsertEntity } from '../stores/entityMutations';
import { consumeNextWindowPlacement } from '../stores/windowPlacement';
import { deriveRouteFromWindow } from '../types/windowRoute';
import { claimCreditCampaignAction } from '../services/creditCampaigns/claimCreditCampaignAction';

// Game apps that qualify for the "open_game" credit-campaign action —
// opening either one earns the same one-time reward (see config/credit_campaigns.py).
const CREDIT_CAMPAIGN_GAME_APP_IDS = new Set(['pong', 'rapids']);

const log = createFileLogger('SPACE_ACTIONS');

export const createSpaceActions = (set: any, get: any) => ({
  /**
   * Create a new window
   * @param params - Window creation parameters
   * @returns Window ID
   */
  createWindow: async (params: {
    appType: string;
    entity?: any;
    title?: string;
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    applicationState?: any;
    props?: Record<string, any>;
  }): Promise<string> => {
    // Credit-campaign: opening a game (Pong/Rapids) qualifies for the
    // "open_game" promo action, if a campaign with that action is currently
    // active. Idempotent on the backend, so firing on every open is safe.
    if (CREDIT_CAMPAIGN_GAME_APP_IDS.has(params.appType)) {
      claimCreditCampaignAction('open_game');
    }

    // ── Entity-dedup guard ────────────────────────────────────────────────────
    // Enforce one tab per entity. This check is synchronous and runs before
    // any window-id generation or state mutation, so concurrent callers (e.g.
    // two AI broadcasts for the same entity in one turn) both see the same
    // windows snapshot and the second call finds the window the first already
    // committed (via the synchronous _applyLocalWindowUpdate below).
    //
    // Skipped for entity-less (app-singleton) windows — those may have multiple
    // tabs (mail, drive, browser, etc.).
    const _dedupEntityId = params.entity?._id || params.entity?.id;
    const _dedupLlmId = params.entity?.llmId;
    if (_dedupEntityId || _dedupLlmId) {
      // appType must match too: some windows (e.g. the Drive browser) mirror their
      // "currently selected/routed entity" onto entityId/entityLlmId without actually
      // being a dedicated viewer for that entity. Matching (and closing!) purely on
      // identity would destroy that unrelated window instead of opening a new one.
      const _existingEntry = Object.entries(get().windows || {}).find(([, w]: any) => {
        const win = w || {};
        const llmMatch = win.entityLlmId && _dedupLlmId && win.entityLlmId === _dedupLlmId;
        const idMatch  = win.entityId    && _dedupEntityId && win.entityId === _dedupEntityId;
        const identityMatch = _dedupLlmId ? (llmMatch && idMatch) : idMatch;
        return identityMatch && win.appType === params.appType;
      });
      if (_existingEntry) {
        const [_existingWindowId] = _existingEntry as [string, any];
        log.info(`[createWindow] Dedup: entity already open in window ${_existingWindowId}, focusing instead of creating duplicate`);
        get().bringWindowToFront(_existingWindowId);
        return _existingWindowId;
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // ✅ FIX: Always generate a proper window ID, never reuse entity llmId
    const windowId = generateWindowId();

    // Honour a one-shot placement request (e.g. Cmd+click on the spatial canvas):
    // the next window created lands at the clicked world point, overriding the
    // caller's default cascade position.
    const placement = consumeNextWindowPlacement();

    log.info(`[createWindow] Creating window: windowId=${windowId} appType=${params.appType}`);
    
    // Build the complete window object with all required fields.
    // entity is intentionally NOT embedded — windows hold only entityId references.
    // getWindowEntity() resolves from the entity registry (the single source of truth).
    const entityId = params.entity?._id || params.entity?.id;
    const entityLlmId = params.entity?.llmId;
    // Resolve size first — the smart position fallback cascades relative to
    // this window's own footprint, so it must know the final size to avoid
    // false-negative overlap checks against differently-sized siblings.
    // Entity-type preferred sizes (e.g. Mail.Thread → compact portrait) always
    // win over a caller-supplied generic size — most open paths pass
    // getWindowSizeAndPosition() without the entity, which would otherwise
    // lock in the landscape default.
    const entityType = params.entity?.entityType || params.entity?.type;
    const preferredSize =
      entityType && ENTITY_TYPE_WINDOW_SIZES[entityType]
        ? resolveDefaultWindowSize({ appType: params.appType, entityType })
        : null;
    const size =
      preferredSize ||
      params.size ||
      resolveDefaultWindowSize({ appType: params.appType, entityType });
    const newWindow: NormalizedWindowEntity = {
      id: windowId,
      type: 'window',
      appType: params.appType,
      entityId,
      entityLlmId,
      position: placement || params.position || computeSmartWindowPosition(size),
      size,
      applicationState: params.applicationState || {},
      props: params.props,
      title: params.title || params.entity?.name || `${params.appType.charAt(0).toUpperCase() + params.appType.slice(1)}`,
    };
    // Backfill canonical route so the window has a route from creation.
    // renderMode from props flows into route.params.mode so apps can read it
    // from the route instead of spreading window.props.
    const derivedRoute = deriveRouteFromWindow(newWindow);
    // AppInstance-backed windows carry a permanent identity — mark them so the
    // router will refuse to overwrite or clear their entityId.
    if (params.entity?.entityType === 'AppInstance' && derivedRoute.params) {
      derivedRoute.params.pinnedIdentity = true;
    }
    (newWindow as any).route = derivedRoute;
    (newWindow as any).routeHistory = [derivedRoute];
    (newWindow as any).routeIndex = 0;
    
    log.info(`[createWindow] 📝 Built window: windowId=${windowId} appType=${newWindow.appType} entityId=${newWindow.entityId || 'none'} title="${newWindow.title}"`);

    // Register entity in the entity store BEFORE adding the window to the space store.
    // windowContents (in Space.tsx) is a useMemo keyed on windowsStableKey — it calls
    // renderWindowContent() the instant the window is added, which calls getWindowEntity().
    // If the entity isn't in the entity store at that exact moment, getWindowEntity() returns
    // null and the result is cached. Moving registration here ensures entity is available.
    // Entities passed to createWindow are already persisted in the backend (fetched via
    // entityService.get()), so we register locally without triggering a CRDT write.
    if (params.entity?._id) {
      try {
        const alreadyInStore = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).some(
          (e: any) => e._id === params.entity!._id
        );
        if (!alreadyInStore) {
          upsertEntity(params.entity, {
            source: 'local',
            syncCRDT: false,
            log: false,
          });
          log.info(`[createWindow] Registered entity in entity store: ${params.entity._id}`);
        }
      } catch { /* non-fatal */ }
    }
    
    // Use the mutation funnel (upsert behavior)
    get()._applyLocalWindowUpdate(windowId, newWindow, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
    
    log.info(`[createWindow] Window added to state: windowId=${windowId} total=${Object.keys(get().windows).length}`);

    // Bring the new window to front — spaceStore.bringWindowToFront also raises
    // the spatialUIStore z-index internally, so the window is visually on top.
    get().bringWindowToFront(windowId);

    // Append the new window to the tab order so it lands at the end of the tab bar.
    // When no order exists yet we seed from the current windows map (lazy migration
    // for existing users who have never dragged a tab). `reorderWindowTabs` is the
    // canonical write-path and handles dedup + stale-id filtering, so we only need
    // to describe the desired sequence here.
    const state = get();
    const existingOrder = state.activeSpace?.settings?.windowTabOrder;
    const baseOrder = (existingOrder && existingOrder.length > 0)
      ? (existingOrder as string[]).filter((id: string) => id !== windowId)
      : Object.keys(state.windows).filter((id: string) => id !== windowId);
    get().reorderWindowTabs([...baseOrder, windowId]);

    // Emit entity.opened for all entity window creations so AgentSupervisor can react.
    // Centralised here so every open path (sidebar, search, chat cards, doc links, etc.)
    // fires the event — fire-and-forget, never fatal.
    if (params.entity?._id) {
      try {
        const { emitEvent, getActiveChatThread } = await import('../services/worldEventsService');
        const spaceId = get().activeSpace?.id || '';
        emitEvent('entity.opened', {
          entityId: params.entity._id,
          entityType: params.entity.entityType,
          name: params.entity.name || '',
          currentThreadId: getActiveChatThread(spaceId),
        }, spaceId);
      } catch { /* non-fatal */ }
    }

    return windowId;
  },

  /**
   * Update an existing window
   * @param windowId - Window ID to update
   * @param updates - Partial window updates
   */
  updateWindow: async (windowId: string, updates: Partial<NormalizedWindowEntity>): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[updateWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[updateWindow] Updating window:', { windowId, updates: Object.keys(updates) });

    // Clean up transient launcher entities (Window, OpenDialog) when navigating away.
    // These are ephemeral entities created by the NewWindow/OpenDialog launcher that must be
    // deleted when the window navigates to real content.
    const oldEntityId = currentWindow.entityId;
    const isNavigatingAway = updates.entityId !== undefined && updates.entityId !== oldEntityId;
    const isSwitchingApp = updates.appType !== undefined && updates.appType !== currentWindow.appType;

    if ((isNavigatingAway || isSwitchingApp) && oldEntityId) {
      const oldEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId
      );
      const isTransientEntity = oldEntity && (
        oldEntity.entityType === 'Window' ||
        oldEntity.entityType === 'OpenDialog'
      );
      if (isTransientEntity) {
        log.info('[updateWindow] Deleting transient launcher entity:', { oldEntityId, entityType: oldEntity.entityType });
        try {
          await entityActions.softDeleteEntity(oldEntityId);
          log.info('[updateWindow] Transient launcher entity deleted:', oldEntity.entityType);
        } catch (error) {
          log.error('[updateWindow] Failed to delete transient entity (continuing):', error);
        }
      }
    }
    
    // Use helper to update window (handles CRDT sync and state update)
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },

  /**
   * Close a window
   * @param windowId - Window ID to close
   */
  closeWindow: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[closeWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[closeWindow] Closing window:', { windowId });
    
    // If window has a Window/OpenDialog entity backing it, soft delete that entity too
    // This includes:
    // 1. entityId - Direct Window/OpenDialog entity (NewWindow, OpenDialog)
    // 2. windowEntityId in applicationState - Tracking entity for regular apps
    if (currentWindow.entityId) {
      const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === currentWindow.entityId
      );
      
      const isWindowEntity = entity && (entity.entityType === 'Window' || entity.entityType === 'OpenDialog');
      if (isWindowEntity) {
        log.info('[closeWindow] Deleting window entity:', {
          entityId: entity._id,
          entityType: entity.entityType,
          entityName: entity.name
        });
        
        try {
          // Use entityActions for proper orchestration (REST + registry + events).
          await entityActions.softDeleteEntity(entity._id);
          log.info('[closeWindow] ✅ Window entity deleted');
        } catch (error) {
          log.warn('[closeWindow] Failed to delete window entity (continuing with window close):', error);
        }
      }
    }
    
    // Also check for windowEntityId in applicationState (for app windows with tracking entities)
    const windowEntityId = currentWindow.applicationState?.windowEntityId;
    if (windowEntityId) {
      log.info('[closeWindow] Deleting tracking Window entity from applicationState:', {
        windowEntityId,
        appType: currentWindow.appType
      });
      
      try {
        await entityActions.softDeleteEntity(windowEntityId);
        log.info('[closeWindow] ✅ Tracking Window entity deleted');
      } catch (error) {
        log.warn('[closeWindow] Failed to delete tracking Window entity:', error);
      }
    }
    
    // ✅ Use removeWindow instead of _applyLocalWindowDelete directly
    // This ensures focus management and child window cleanup work properly
    get().removeWindow(windowId);
  },

  /**
   * Set active window.
   * Delegates to the top-level setActiveWindow on the store, which raises the
   * window's z-index via bringWindowToFront (unless clearing to null) and
   * handles localStorage/backend persistence.
   */
  setActiveWindow: (windowId: string | null): void => {
    log.info('[setActiveWindow] Setting active window:', windowId);
    get().setActiveWindow(windowId);
  },

  /**
   * Navigate window to a new entity (adds to navigation history).
   * Delegates to windowRouter.navigate which is the canonical path.
   */
  navigateWindow: async (windowId: string, entityId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];

    if (!currentWindow) {
      // Actionable error — include available windowIds so the caller can diagnose.
      const available = Object.keys(get().windows ?? {});
      log.error('[navigateWindow] Window not found — cannot navigate', {
        windowId,
        availableWindows: available.join(', ') || '(none)',
      });
      return;
    }

    log.info(`[navigateWindow] Delegating to windowRouter: windowId=${windowId} entityId=${entityId}`);

    // Resolve entity (needed to pick correct appType for launchers).
    let entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId,
    );
    if (!entity) {
      try {
        const { entityService } = await import('../services/entityService');
        entity = await entityService.get(entityId);
      } catch (error) {
        log.warn('[navigateWindow] Fetch fallback failed for entity:', entityId, error);
      }
    }
    if (!entity) {
      log.warn('[navigateWindow] Entity not found:', entityId);
      return;
    }

    // Launcher windows resolve their appType from the target entity.
    const shouldReplaceLauncherApp =
      currentWindow.appType === 'new-window' ||
      currentWindow.appType === 'entity-view' ||
      (currentWindow.applicationState as any)?.isNew === true;
    let appType = currentWindow.appType ?? 'entity-view';
    if (shouldReplaceLauncherApp) {
      const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
      appType = getAppTypeForEntity(entity);
    }

    const { windowRouter } = await import('./windowRouter');
    const route = {
      appType,
      params: {
        entityId: entity._id || entity.id,
        entityLlmId: entity.llmId,
      },
    };
    await windowRouter.navigate(windowId, route);
  },

  /**
   * Clear the entityId (and optionally reset the title) from a window.
   * Delegates to windowRouter.replace with entityId: undefined so the route
   * is kept coherent. Syncs to CRDT via the router.
   */
  clearWindowEntityId: (windowId: string, fallbackTitle?: string): void => {
    const currentWindow = get().windows[windowId];
    if (!currentWindow) return;

    // Skip if there is nothing to clear and the title already matches — true no-op.
    const titleAlreadyCurrent = !fallbackTitle || currentWindow.title === fallbackTitle;
    if (!currentWindow.entityId && titleAlreadyCurrent) return;

    log.info(`[clearWindowEntityId] Delegating to windowRouter: windowId=${windowId} clearedId=${currentWindow.entityId} fallbackTitle="${fallbackTitle || ''}"`);

    // Delegate asynchronously — replace is non-blocking for the caller.
    // We must update the title synchronously here as well so immediate re-renders
    // (e.g. tab title) see the fallback immediately before reconcile settles.
    const titleUpdates: Partial<import('./spaceStore').NormalizedWindowEntity> = {
      entityId: undefined,
      entityLlmId: undefined,
    };
    if (fallbackTitle !== undefined) {
      titleUpdates.title = fallbackTitle;
    }
    get()._applyLocalWindowUpdate(windowId, titleUpdates, {
      source: 'local',
      syncCRDT: true,
      log: false,
    });

    // Also update the route so it stays coherent with the cleared entity.
    import('./windowRouter').then(({ windowRouter }) => {
      windowRouter.replace(windowId, { params: { entityId: undefined, entityLlmId: undefined } }, { fallbackTitle }).catch(() => {});
    }).catch(() => {});
  },

  /**
   * Navigate back in window history. Delegates to windowRouter.back.
   */
  navigateBack: async (windowId: string): Promise<void> => {
    log.info('[navigateBack] Delegating to windowRouter:', { windowId });
    const { windowRouter } = await import('./windowRouter');
    await windowRouter.back(windowId);
  },

  /**
   * Navigate forward in window history. Delegates to windowRouter.forward.
   */
  navigateForward: async (windowId: string): Promise<void> => {
    log.info('[navigateForward] Delegating to windowRouter:', { windowId });
    const { windowRouter } = await import('./windowRouter');
    await windowRouter.forward(windowId);
  },
});
